import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Shield, UserCheck, UserX, Users, Flag, CheckCircle2, LayoutDashboard } from "lucide-react";
import AdminDashboard from "../components/AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  approved: { hex: "#2ecc71", bg: "rgba(46,204,113,0.08)",  border: "rgba(46,204,113,0.35)" },
  pending:  { hex: "#f39c12", bg: "rgba(243,156,18,0.08)",   border: "rgba(243,156,18,0.35)" },
  rejected: { hex: "#ef4444", bg: "rgba(239,68,68,0.08)",    border: "rgba(239,68,68,0.35)" },
};

function StatusPill({ status }) {
  const st = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold border" style={{ color: st.hex, background: st.bg, borderColor: st.border }}>{status}</span>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState("dashboard");
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState("");

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users/pending`, { withCredentials: true });
      setPendingUsers(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch pending users:", err);
      setError("Ausstehende User konnten nicht geladen werden.");
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reports/?status=open`, { withCredentials: true });
      setReports(data);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    }
  }, []);

  const resolveReport = async (id) => {
    const note = window.prompt("Hinweis zur Bearbeitung (optional):", "") || "";
    try {
      await axios.post(`${API}/reports/${id}/resolve`, { note }, { withCredentials: true });
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err?.response?.data?.detail || "Meldung konnte nicht abgeschlossen werden.");
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setAllUsers(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch all users:", err);
      setError("User konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => { fetchPending(); fetchAll(); fetchReports(); }, [fetchPending, fetchAll, fetchReports]);

  const handleApprove = async (userId) => {
    await axios.post(`${API}/admin/users/${userId}/approve`, {}, { withCredentials: true });
    fetchPending(); fetchAll();
  };
  const handleReject = async (userId) => {
    await axios.post(`${API}/admin/users/${userId}/reject`, {}, { withCredentials: true });
    fetchPending(); fetchAll();
  };
  const handleRoleChange = async (userId, role) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Rolle konnte nicht geändert werden.");
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "";

  const approvedCount = allUsers.filter((u) => u.status === "approved").length;
  const pendingCount = pendingUsers.length;
  const adminCount = allUsers.filter((u) => u.role === "admin").length;

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          {/* Hero */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#a855f7]" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#a855f7]/90 font-semibold">Admin-Bereich</span>
            </div>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="admin-heading">
              <span className="text-[#a855f7]">Community</span> Verwaltung
            </h1>
            <p className="text-base sm:text-lg text-white/50 leading-relaxed">Freischaltungen, Nutzerverwaltung, Plattform-Health.</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-px bg-white/[0.05] mb-8 border border-white/[0.06]">
            {[
              { num: pendingCount, label: "Wartend", sub: "auf Freischaltung", color: "#f39c12", icon: UserCheck },
              { num: approvedCount, label: "Aktiv", sub: "freigeschaltet", color: "#2ecc71", icon: Users },
              { num: adminCount, label: "Admins", sub: "Super-User", color: "#a855f7", icon: Shield },
            ].map((s) => (
              <div key={s.label} className="bg-black px-4 sm:px-6 py-5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <s.icon size={15} className="text-white/30" strokeWidth={1.5} />
                </div>
                <div className="font-outfit font-black text-3xl sm:text-4xl tracking-tighter" style={{ color: s.color }}>{s.num}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/55 font-outfit font-medium">{s.label}</div>
                <div className="text-[12px] text-white/30 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 flex-wrap" data-testid="admin-tabs">
            {[
              { id: "dashboard", label: "Dashboard",                           color: "#00e5ff", testId: "admin-tab-dashboard" },
              { id: "pending",   label: `Ausstehend (${pendingUsers.length})`, color: "#f39c12", testId: "admin-tab-pending" },
              { id: "all",       label: `Alle Nutzer (${allUsers.length})`,    color: "#a855f7", testId: "admin-tab-all" },
              { id: "reports",   label: `Meldungen (${reports.length})`,       color: "#ef4444", testId: "admin-tab-reports" },
            ].map((t) => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testId}
                  className="px-4 sm:px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] font-outfit font-semibold whitespace-nowrap transition-all duration-300 border inline-flex items-center gap-2"
                  style={{ color: active ? t.color : "rgba(255,255,255,0.5)", borderColor: active ? `${t.color}55` : "rgba(255,255,255,0.08)", background: active ? `${t.color}0d` : "transparent" }}>
                  {t.id === "dashboard" && <LayoutDashboard size={12} />}
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {tab === "dashboard" && <AdminDashboard />}
          {tab === "pending" && (
            <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.06]" data-testid="pending-list">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-20">
                  <UserCheck size={28} className="text-[#2ecc71]/40 mx-auto mb-3" strokeWidth={1.3} />
                  <p className="text-white/80 text-lg font-outfit font-semibold">Alles erledigt</p>
                  <p className="text-[14px] text-white/40 mt-1.5">Keine ausstehenden Anfragen.</p>
                </div>
              ) : (
                pendingUsers.map((u, idx) => (
                  <div key={u.id} className={`px-5 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${idx < pendingUsers.length - 1 ? "border-b border-white/[0.05]" : ""}`} data-testid={`pending-user-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 flex items-center justify-center border border-white/15 bg-white/[0.03] text-[11px] uppercase font-outfit font-bold text-white/80 tracking-wider shrink-0">
                        {(u.first_name?.[0] || "").toUpperCase()}{(u.last_name?.[0] || "").toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[15px] text-white font-outfit font-semibold tracking-tight">{u.first_name} {u.last_name}</p>
                        <p className="text-[12px] text-white/40 truncate">{u.email} · {u.company || "Kein Unternehmen"} · {formatDate(u.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button onClick={() => handleApprove(u.id)} data-testid={`approve-${u.id}`} size="sm" className="bg-[#2ecc71]/10 text-[#2ecc71] hover:bg-[#2ecc71]/20 border border-[#2ecc71]/40 hover:border-[#2ecc71]/60 rounded-none h-10 px-3.5 text-[12px] uppercase tracking-wider font-outfit font-semibold transition-all">
                        <UserCheck size={14} className="mr-1.5" />Freischalten
                      </Button>
                      <Button onClick={() => handleReject(u.id)} data-testid={`reject-${u.id}`} size="sm" className="bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 border border-[#ef4444]/40 hover:border-[#ef4444]/60 rounded-none h-10 px-3.5 text-[12px] uppercase tracking-wider font-outfit font-semibold transition-all">
                        <UserX size={14} className="mr-1.5" />Ablehnen
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "all" && (
            <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.06]" data-testid="all-users-list">
              <div className="grid grid-cols-12 gap-3 px-5 sm:px-6 py-3 border-b border-white/[0.06] text-[11px] text-white/45 uppercase tracking-[0.2em] font-outfit font-semibold bg-white/[0.015]">
                <div className="col-span-4">Name</div>
                <div className="col-span-3 hidden sm:block">E-Mail</div>
                <div className="col-span-2 hidden md:block">Firma</div>
                <div className="col-span-2">Rolle</div>
                <div className="col-span-3 sm:col-span-1">Status</div>
              </div>
              {allUsers.map((u, idx) => (
                <div key={u.id} className={`grid grid-cols-12 gap-3 px-5 sm:px-6 py-4 items-center text-[14px] hover:bg-white/[0.015] transition-colors ${idx < allUsers.length - 1 ? "border-b border-white/[0.05]" : ""}`} data-testid={`user-row-${u.id}`}>
                  <div className="col-span-9 sm:col-span-4 flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center border border-white/15 bg-white/[0.03] text-[10px] uppercase font-outfit font-bold text-white/80 tracking-wider shrink-0">
                      {(u.first_name?.[0] || "").toUpperCase()}{(u.last_name?.[0] || "").toUpperCase()}
                    </div>
                    <span className="text-white font-outfit font-medium tracking-tight truncate">{u.first_name} {u.last_name}</span>
                  </div>
                  <div className="col-span-3 hidden sm:block text-white/55 truncate text-[13px]">{u.email}</div>
                  <div className="col-span-2 hidden md:block text-white/45 truncate text-[13px]">{u.company || "—"}</div>
                  <div className="col-span-2">
                    <select
                      value={u.role || "user"}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      data-testid={`role-select-${u.id}`}
                      className="bg-black border border-white/15 text-white text-[12px] uppercase tracking-wider font-outfit font-semibold h-8 px-2 focus:border-[#a855f7]/60 focus:outline-none rounded-none"
                    >
                      <option value="user">user</option>
                      <option value="speaker">speaker</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                  <div className="col-span-3 sm:col-span-1 flex items-center gap-2 flex-wrap">
                    <StatusPill status={u.status} />
                    {u.status === "pending" && (
                      <button onClick={() => handleApprove(u.id)} className="text-[#2ecc71]/60 hover:text-[#2ecc71]" title="Freischalten"><UserCheck size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "reports" && (
            <div className="bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.06]" data-testid="reports-list">
              {reports.length === 0 ? (
                <div className="text-center py-20">
                  <Flag size={28} className="text-[#ef4444]/40 mx-auto mb-3" strokeWidth={1.3} />
                  <p className="text-white/80 text-lg font-outfit font-semibold">Keine offenen Meldungen</p>
                  <p className="text-[14px] text-white/40 mt-1.5">Sobald ein Mitglied etwas meldet, erscheint es hier.</p>
                </div>
              ) : (
                reports.map((r, idx) => (
                  <div key={r.id} className={`px-5 sm:px-6 py-4 flex flex-col gap-3 ${idx < reports.length - 1 ? "border-b border-white/[0.05]" : ""}`} data-testid={`report-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/40">
                          {r.target_type}
                        </span>
                        <span className="text-white font-outfit font-semibold">{r.target_name || "—"}</span>
                        {r.target_id && <span className="text-[11px] text-white/35 font-mono">({r.target_id})</span>}
                      </div>
                      <span className="text-[11px] text-white/40 font-outfit">{formatDate(r.created_at)}</span>
                    </div>
                    <div className="border-l-2 border-red-400/40 pl-3 text-[14px] text-white/80 leading-relaxed whitespace-pre-wrap">{r.reason}</div>
                    <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                      <p className="text-[12px] text-white/45">Gemeldet von <b className="text-white/75">{r.reporter_name}</b> · {r.reporter_email}</p>
                      <Button onClick={() => resolveReport(r.id)} data-testid={`resolve-${r.id}`} size="sm"
                        className="bg-[#2ecc71]/10 text-[#2ecc71] hover:bg-[#2ecc71]/20 border border-[#2ecc71]/40 rounded-none h-9 px-3 text-[11px] uppercase tracking-wider font-outfit font-semibold">
                        <CheckCircle2 size={13} className="mr-1.5" />Abgeschlossen
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
